import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../Libs/firebase-config.mjs';
import { Images, ChevronLeft, ChevronRight, Play, ExternalLink, RefreshCw, X, ZoomIn } from 'lucide-react';

/* ─── CDN / IMAGE OPTIMIZATION ──────────────────────────── */
function buildCdnUrl(src, width) {
  if (!src) return src;
  if (src.includes('res.cloudinary.com')) {
    return src.replace('/upload/', `/upload/w_${width},q_auto,f_auto/`);
  }
  if (src.includes('.imgix.net')) {
    const url = new URL(src);
    url.searchParams.set('w', width);
    url.searchParams.set('auto', 'format,compress');
    url.searchParams.set('fit', 'max');
    return url.toString();
  }
  if (src.includes('firebasestorage.googleapis.com') || src.includes('storage.googleapis.com')) {
    const sep = src.includes('?') ? '&' : '?';
    return `${src}${sep}w=${width}`;
  }
  try {
    const url = new URL(src);
    url.searchParams.set('w', width);
    return url.toString();
  } catch {
    return src;
  }
}

function buildSrcSet(src) {
  const widths = [480, 800, 1200, 1600];
  return widths.map(w => `${buildCdnUrl(src, w)} ${w}w`).join(', ');
}

function getSizes(cols) {
  if (cols === 1) return '100vw';
  if (cols === 2) return '50vw';
  if (cols === 3) return '33vw';
  return '25vw';
}

/* ─── IN-MEMORY IMAGE CACHE ──────────────────────────────── */
const imageCache = new Set();
function markCached(src) { imageCache.add(src); }
function isCached(src) { return imageCache.has(src); }

/* ─── NATURAL DIMENSION CACHE ────────────────────────────── 
   Stores { w, h } for every src we've measured so we can
   classify the aspect ratio without re-probing on re-renders.
──────────────────────────────────────────────────────────── */
const dimCache = new Map(); // src → { w, h }

function probeImageDimensions(src) {
  return new Promise((resolve) => {
    if (dimCache.has(src)) { resolve(dimCache.get(src)); return; }
    const img = new window.Image();
    img.onload = () => {
      const dims = { w: img.naturalWidth, h: img.naturalHeight };
      dimCache.set(src, dims);
      resolve(dims);
    };
    img.onerror = () => resolve(null);
    // Use a small version to probe quickly (we only need the ratio)
    img.src = buildCdnUrl(src, 480);
  });
}

function classifyAspect(w, h) {
  if (!w || !h) return 'square';
  const ratio = w / h;
  if (ratio >= 1.6)  return 'landscape'; // very wide
  if (ratio >= 1.15) return 'wide';      // slightly wider than square
  if (ratio >= 0.87) return 'square';    // near square
  if (ratio >= 0.6)  return 'portrait';  // tall
  return 'tall';                          // very tall / 9:16
}

const ASPECT_RATIO_MAP = {
  portrait:  '3/4',
  landscape: '16/9',
  wide:      '4/3',
  square:    '1/1',
  tall:      '9/16',
};

// Fallback cycle for items whose dimensions haven't loaded yet
const ASPECT_CYCLE = [
  'portrait', 'landscape', 'square', 'portrait',
  'landscape', 'tall', 'square', 'landscape', 'portrait',
];

function assignAspects(items) {
  return items.map((item, i) => ({
    ...item,
    // If already in dimCache, classify immediately; otherwise fall back to cycle
    _aspect: dimCache.has(item.src)
      ? classifyAspect(dimCache.get(item.src).w, dimCache.get(item.src).h)
      : (item.aspect && ASPECT_RATIO_MAP[item.aspect]
          ? item.aspect
          : ASPECT_CYCLE[i % ASPECT_CYCLE.length]),
    _dimensionsResolved: dimCache.has(item.src),
  }));
}

/* ─── YOUTUBE HELPERS ────────────────────────────────────── */
function getYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtu\.be\/([^?]+)/,
    /youtube\.com\/embed\/([^?]+)/,
    /youtube\.com\/shorts\/([^?]+)/,
  ];
  for (const p of patterns) { const m = url.match(p); if (m) return m[1]; }
  return null;
}
const getYouTubeThumbnail = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
const isYouTubeUrl = (url) => !!(url && (url.includes('youtube.com') || url.includes('youtu.be')));

/* ─── TAG COLOURS ─────────────────────────────────────────── */
const TAG_COLORS = {
  'Training Session':  '#2563eb',
  'Farm Visit':        '#285020',
  'Expert Talk':       '#7c3aed',
  'Community Event':   '#d97706',
  'Church Activation': '#0891b2',
  'Graduation':        '#dc2626',
  'Field Day':         '#0f766e',
  'Market Day':        '#c2410c',
  'Workshop':          '#7c3aed',
  'T1 Training':       '#2563eb',
  'T2 Expert':         '#7c3aed',
  'T3 Farm Visit':     '#285020',
  'Expert Session':    '#7c3aed',
  'Activation':        '#d97706',
  'Community':         '#0891b2',
};
const getTagColor = (t) => TAG_COLORS[t] || '#285020';

const PAGE_SIZE = 12;

/* ─── GLOBAL CSS INJECTION ───────────────────────────────── */
const injectedStyles = new Set();

function injectCSS(isDark) {
  const key = `mg-gallery-${isDark ? 'dark' : 'light'}`;
  if (injectedStyles.has(key)) return;
  injectedStyles.add(key);

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Syne:wght@400;600;700;800&display=swap');

    @keyframes mgFadeUp    { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
    @keyframes mgShimmer   { 0%{background-position:-800px 0} 100%{background-position:800px 0} }
    @keyframes mgScaleIn   { from{opacity:0;transform:scale(0.94)} to{opacity:1;transform:scale(1)} }
    @keyframes mgPulse     { 0%,100%{opacity:1} 50%{opacity:.45} }

    .mg-root * { box-sizing: border-box; }

    .mg-masonry { column-gap: 14px; }

    .mg-card {
      break-inside: avoid;
      overflow: hidden;
      border-radius: 10px;
      position: relative;
      display: block;
      margin-bottom: 14px;
      cursor: zoom-in;
      will-change: transform;
      transition: transform 0.3s cubic-bezier(0.16,1,0.3,1), box-shadow 0.3s ease;
      background: ${isDark ? '#1c2431' : '#e2e8f0'};
    }
    .mg-card:hover {
      transform: translateY(-4px) scale(1.008);
      box-shadow: 0 24px 56px ${isDark ? 'rgba(0,0,0,0.55)' : 'rgba(15,118,110,0.2)'} !important;
      z-index: 3;
    }
    /* 
      KEY CHANGE: images now use object-fit: contain instead of cover
      so nothing gets cropped — the full image is always visible.
      The card background shows through as a subtle letterbox.
    */
    .mg-card img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
      transition: transform 0.6s cubic-bezier(0.16,1,0.3,1), opacity 0.4s ease;
    }
    .mg-card:hover img { transform: scale(1.04); }
    .mg-card:hover .mg-zoom-icon { opacity: 1 !important; }

    .mg-shimmer {
      position: absolute; inset: 0;
      background: linear-gradient(90deg,
        ${isDark ? '#1c2431 25%, #2a3444 50%, #1c2431 75%' : '#e2e8f0 25%, #cbd5e1 50%, #e2e8f0 75%'});
      background-size: 800px 100%;
      animation: mgShimmer 1.6s ease-in-out infinite;
    }

    .mg-overlay {
      position: absolute; bottom: 0; left: 0; right: 0;
      padding: 3.5rem 0.9rem 0.8rem;
      background: linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.2) 65%, transparent 100%);
      pointer-events: none;
      transition: opacity 0.3s ease;
    }

    .mg-tag {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-family: 'Syne', sans-serif;
      font-weight: 700;
      font-size: 0.58rem;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      color: #fff;
      padding: 0.2rem 0.55rem;
      border-radius: 4px;
    }

    .mg-filter {
      display: flex; align-items: center; gap: 0.35rem;
      padding: 0.42rem 0.9rem;
      border-radius: 6px;
      font-family: 'Syne', sans-serif;
      font-weight: 700;
      font-size: 0.72rem;
      cursor: pointer;
      transition: all 0.18s ease;
      letter-spacing: 0.03em;
    }
    .mg-filter:hover { transform: translateY(-2px); }

    .mg-pgbtn {
      width: 36px; height: 36px;
      border-radius: 6px;
      font-family: 'Syne', sans-serif;
      font-weight: 700;
      font-size: 0.78rem;
      cursor: pointer;
      transition: all 0.16s ease;
      display: flex; align-items: center; justify-content: center;
    }
    .mg-pgbtn:hover:not(:disabled) { transform: scale(1.1); }
    .mg-pgbtn:disabled { opacity: 0.3; cursor: default; }

    .mg-skel {
      break-inside: avoid;
      border-radius: 10px;
      margin-bottom: 14px;
      animation: mgPulse 1.7s ease-in-out infinite;
    }

    /* ── Lightbox ── */
    .mg-lightbox-backdrop {
      position: fixed; inset: 0; z-index: 9999;
      background: rgba(0,0,0,0.93);
      display: flex; align-items: center; justify-content: center;
      animation: mgScaleIn 0.22s ease;
      backdrop-filter: blur(12px);
    }
    /*
      Lightbox image: show full image without any cropping.
      max-width/max-height + object-fit:contain ensures the whole
      image is visible regardless of its natural orientation.
    */
    .mg-lightbox-img {
      max-width: min(92vw, 1200px);
      max-height: 88vh;
      width: auto;
      height: auto;
      object-fit: contain;
      border-radius: 8px;
      box-shadow: 0 32px 80px rgba(0,0,0,0.8);
      animation: mgScaleIn 0.28s cubic-bezier(0.16,1,0.3,1);
      display: block;
    }
    .mg-lightbox-close {
      position: absolute;
      top: 20px; right: 24px;
      width: 40px; height: 40px;
      border-radius: 50%;
      background: rgba(255,255,255,0.12);
      border: 1px solid rgba(255,255,255,0.22);
      color: #fff;
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: background 0.18s ease;
    }
    .mg-lightbox-close:hover { background: rgba(255,255,255,0.22); }

    /* Navigation arrows */
    .mg-lightbox-nav {
      position: absolute;
      top: 50%; transform: translateY(-50%);
      width: 44px; height: 44px;
      border-radius: 50%;
      background: rgba(255,255,255,0.12);
      border: 1px solid rgba(255,255,255,0.22);
      color: #fff;
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: background 0.18s ease;
      z-index: 10;
    }
    .mg-lightbox-nav:hover { background: rgba(255,255,255,0.26); }

    .mg-video-card {
      break-inside: avoid;
      cursor: pointer;
      border-radius: 10px;
      overflow: hidden;
      position: relative;
      display: block;
      margin-bottom: 14px;
      will-change: transform;
      transition: transform 0.3s cubic-bezier(0.16,1,0.3,1), box-shadow 0.3s ease;
      background: ${isDark ? '#1c2431' : '#e2e8f0'};
    }
    .mg-video-card:hover {
      transform: translateY(-4px) scale(1.008);
      box-shadow: 0 24px 56px ${isDark ? 'rgba(0,0,0,0.55)' : 'rgba(15,118,110,0.2)'} !important;
      z-index: 3;
    }
    .mg-video-card:hover img { transform: scale(1.06); }
    .mg-video-card img {
      width: 100%; height: 100%; object-fit: cover; display: block;
      transition: transform 0.6s cubic-bezier(0.16,1,0.3,1), opacity 0.4s ease;
    }

    .mg-count-badge {
      font-family: 'Cormorant Garamond', serif;
      font-weight: 700;
      font-size: 0.65rem;
      padding: 0.08rem 0.45rem;
      border-radius: 3px;
    }

    /* Preload link hints are injected inline; no CSS needed here */
  `;

  const el = document.createElement('style');
  el.id = key;
  el.textContent = css;
  document.head.appendChild(el);
}

/* ─── PRELOAD HINT ───────────────────────────────────────── 
   On hover, inject a <link rel="preload"> so the browser
   fetches the high-res image immediately, before the click.
──────────────────────────────────────────────────────────── */
const preloadedUrls = new Set();
function preloadImage(src) {
  const url = buildCdnUrl(src, 1600);
  if (!url || preloadedUrls.has(url)) return;
  preloadedUrls.add(url);

  // Use Image() constructor — faster than a <link> element and
  // writes directly into the browser's disk cache.
  const img = new window.Image();
  img.src = url;
}

/* ─── SKELETON LOADER ─────────────────────────────────────── */
function SkeletonGrid({ cols, isDark }) {
  const heights = ['66%', '50%', '80%', '56%', '70%', '50%', '60%', '85%', '50%', '66%', '75%', '56%'];
  return (
    <div className="mg-masonry" style={{ columnCount: cols }}>
      {heights.slice(0, PAGE_SIZE).map((h, i) => (
        <div key={i} className="mg-skel" style={{ paddingBottom: h, background: isDark ? '#1c2431' : '#e2e8f0' }} />
      ))}
    </div>
  );
}

/* ─── PROGRESSIVE IMAGE ───────────────────────────────────── */
const ProgressiveImage = memo(function ProgressiveImage({ src, alt, style, sizes, onLoad: onLoadProp, onNaturalSize }) {
  const [status, setStatus] = useState(() => isCached(src) ? 'loaded' : 'loading');
  const imgRef = useRef(null);
  const srcRef = useRef(src);

  const handleLoad = useCallback(() => {
    markCached(src);
    setStatus('loaded');
    onLoadProp?.();
    // Report natural dimensions so the parent can refine the aspect ratio
    if (onNaturalSize && imgRef.current) {
      const { naturalWidth: w, naturalHeight: h } = imgRef.current;
      if (w && h) {
        dimCache.set(src, { w, h });
        onNaturalSize(w, h);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const handleError = useCallback(() => setStatus('failed'), []);

  useEffect(() => {
    if (!isCached(src)) {
      setStatus('loading');
    } else {
      setStatus('loaded');
    }
  }, [src]);

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth) {
      handleLoad();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {status === 'loading' && <div className="mg-shimmer" />}
      {status !== 'failed' && (
        <img
          ref={imgRef}
          src={buildCdnUrl(src, 800)}
          srcSet={buildSrcSet(src)}
          sizes={sizes || '33vw'}
          alt={alt || ''}
          style={{ ...style, opacity: status === 'loaded' ? 1 : 0 }}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          decoding="async"
        />
      )}
      {status === 'failed' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
          <Images size={24} style={{ opacity: 0.3 }} />
          <span style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.65rem', opacity: 0.4 }}>Unavailable</span>
        </div>
      )}
    </>
  );
});

/* ─── IMAGE CARD ─────────────────────────────────────────── */
const ImageCard = memo(function ImageCard({ item, index, isDark, cols, onLightbox, onDimensionResolved }) {
  // Local aspect state — starts from whatever assignAspects gave us,
  // then updates once the image loads and we know the real dimensions.
  const [aspect, setAspect] = useState(item._aspect || 'portrait');
  const aspectRatio = ASPECT_RATIO_MAP[aspect] || '3/4';
  const tagColor = getTagColor(item.tag);
  const delay = (index % PAGE_SIZE) * 0.04;

  const handleClick = useCallback(() => onLightbox(item), [item, onLightbox]);

  // Called by ProgressiveImage once the thumbnail loads
  const handleNaturalSize = useCallback((w, h) => {
    const detected = classifyAspect(w, h);
    setAspect(detected);
    onDimensionResolved?.(item.src, detected);
  }, [item.src, onDimensionResolved]);

  return (
    <div
      className="mg-card"
      style={{
        boxShadow: isDark ? '0 2px 14px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.06)',
        animation: `mgFadeUp 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}s both`,
      }}
      onClick={handleClick}
      // Preload full-res on hover so lightbox feels instant
      onMouseEnter={() => preloadImage(item.src)}
      onFocus={() => preloadImage(item.src)}
    >
      <div style={{ position: 'relative', aspectRatio, overflow: 'hidden' }}>
        <ProgressiveImage
          src={item.src}
          alt={item.caption || item.tag || ''}
          sizes={getSizes(cols)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          onNaturalSize={handleNaturalSize}
        />

        <div
          className="mg-zoom-icon"
          style={{
            position: 'absolute', top: 10, right: 10,
            width: 28, height: 28, borderRadius: '50%',
            background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0, transition: 'opacity 0.2s ease',
          }}
        >
          <ZoomIn size={12} color="#fff" />
        </div>

        <div className="mg-overlay">
          {item.caption && (
            <p style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.73rem', color: 'rgba(255,255,255,0.92)', margin: '0 0 0.45rem', lineHeight: 1.45, fontWeight: 600 }}>
              {item.caption}
            </p>
          )}
          {item.tag && (
            <div className="mg-tag" style={{ background: tagColor }}>
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.65)', flexShrink: 0 }} />
              {item.tag}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

/* ─── VIDEO CARD ──────────────────────────────────────────── */
const VideoCard = memo(function VideoCard({ item, index, isDark, cols }) {
  const ytId = isYouTubeUrl(item.src) ? getYouTubeId(item.src) : null;
  const thumb = ytId ? getYouTubeThumbnail(ytId) : (item.poster || '');
  const [loaded, setLoaded] = useState(() => isCached(thumb));
  const tagColor = getTagColor(item.tag);
  const aspectRatio = ASPECT_RATIO_MAP[item._aspect] || '16/9';
  const delay = (index % PAGE_SIZE) * 0.04;

  const handleLoad = useCallback(() => { markCached(thumb); setLoaded(true); }, [thumb]);

  const handleClick = useCallback(() => {
    if (ytId) {
      window.open(`https://www.youtube.com/watch?v=${ytId}`, '_blank', 'noopener,noreferrer');
    } else if (item.src) {
      window.open(item.src, '_blank', 'noopener,noreferrer');
    }
  }, [ytId, item.src]);

  return (
    <div
      className="mg-video-card"
      style={{
        boxShadow: isDark ? '0 2px 14px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.06)',
        animation: `mgFadeUp 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}s both`,
      }}
      onClick={handleClick}
    >
      <div style={{ position: 'relative', aspectRatio, overflow: 'hidden' }}>
        {!loaded && <div className="mg-shimmer" />}
        {thumb && (
          <img
            src={thumb}
            alt={item.caption || item.tag || ''}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: loaded ? 1 : 0, transition: 'opacity 0.4s ease' }}
            onLoad={handleLoad}
            onError={() => setLoaded(true)}
            loading="lazy"
          />
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)', pointerEvents: 'none' }} />

        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: ytId ? 'rgba(255,0,0,0.9)' : 'rgba(22,163,74,0.9)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 24px rgba(0,0,0,0.45)',
            border: '2px solid rgba(255,255,255,0.4)',
          }}>
            <Play size={20} color="#fff" fill="#fff" style={{ marginLeft: 3 }} />
          </div>
        </div>

        {ytId && (
          <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(255,0,0,0.9)', backdropFilter: 'blur(4px)', color: '#fff', fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: '0.52rem', padding: '0.18rem 0.5rem', borderRadius: '4px', letterSpacing: '0.07em' }}>
            ▶ YOUTUBE
          </div>
        )}

        <div className="mg-overlay">
          {item.caption && (
            <p style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.73rem', color: 'rgba(255,255,255,0.92)', margin: '0 0 0.45rem', lineHeight: 1.45, fontWeight: 600 }}>
              {item.caption}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {item.tag && (
              <div className="mg-tag" style={{ background: tagColor }}>
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.65)', flexShrink: 0 }} />
                {item.tag}
              </div>
            )}
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 'auto' }}>
              <ExternalLink size={9} color="#fff" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

/* ─── LIGHTBOX ────────────────────────────────────────────── 
   Changes vs original:
   1. Uses `width: auto; height: auto; max-width/max-height` so
      the image renders at its natural proportions — no cropping.
   2. Keyboard arrow navigation between images in the filtered list.
   3. On open, the full-res URL is likely already in browser cache
      (preloaded on hover), so it paints almost immediately.
──────────────────────────────────────────────────────────── */
const Lightbox = memo(function Lightbox({ item, items, onClose, onNav }) {
  const currentIndex = items ? items.findIndex(i => i.src === item.src) : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < (items?.length ?? 0) - 1;

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft'  && hasPrev) onNav(currentIndex - 1);
      if (e.key === 'ArrowRight' && hasNext) onNav(currentIndex + 1);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, onNav, currentIndex, hasPrev, hasNext]);

  if (!item) return null;

  return (
    <div className="mg-lightbox-backdrop" onClick={onClose}>
      <button className="mg-lightbox-close" onClick={onClose}><X size={16} /></button>

      {hasPrev && (
        <button
          className="mg-lightbox-nav"
          style={{ left: 16 }}
          onClick={e => { e.stopPropagation(); onNav(currentIndex - 1); }}
        >
          <ChevronLeft size={18} />
        </button>
      )}

      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
        <img
          className="mg-lightbox-img"
          /* Use the highest quality URL — likely already preloaded in cache */
          src={buildCdnUrl(item.src, 1600)}
          srcSet={buildSrcSet(item.src)}
          sizes="min(92vw, 1200px)"
          alt={item.caption || item.tag || ''}
          /* eager + high fetchpriority to override any lazy hints */
          loading="eager"
          fetchpriority="high"
          decoding="sync"
        />
        {(item.caption || item.tag) && (
          <div style={{ textAlign: 'center' }}>
            {item.caption && (
              <p style={{ fontFamily: "'Syne', sans-serif", color: 'rgba(255,255,255,0.88)', fontSize: '0.88rem', margin: '0 0 0.3rem', fontWeight: 600 }}>
                {item.caption}
              </p>
            )}
            {item.tag && (
              <div className="mg-tag" style={{ background: getTagColor(item.tag), display: 'inline-flex' }}>
                {item.tag}
              </div>
            )}
          </div>
        )}
        {items && items.length > 1 && (
          <p style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', margin: 0, letterSpacing: '0.08em' }}>
            {currentIndex + 1} / {items.length}
          </p>
        )}
      </div>

      {hasNext && (
        <button
          className="mg-lightbox-nav"
          style={{ right: 16 }}
          onClick={e => { e.stopPropagation(); onNav(currentIndex + 1); }}
        >
          <ChevronRight size={18} />
        </button>
      )}
    </div>
  );
});

/* ─── PAGINATION ──────────────────────────────────────────── */
const Pagination = memo(function Pagination({ current, total, onChange, isDark }) {
  if (total <= 1) return null;
  const accent = '#0f766e';
  const border = isDark ? 'rgba(255,255,255,0.1)' : '#cbd5e1';
  const bg     = isDark ? 'rgba(255,255,255,0.04)' : '#fff';
  const text   = isDark ? 'rgba(255,255,255,0.6)' : '#475569';

  const pages = Array.from({ length: total }, (_, i) => i + 1);
  const half = 2;
  let visible = pages;
  if (total > 5) {
    let s = Math.max(0, current - 1 - half);
    let e = s + 5;
    if (e > total) { e = total; s = Math.max(0, e - 5); }
    visible = pages.slice(s, e);
  }

  const btn = (disabled, onClick, children) => (
    <button className="mg-pgbtn" disabled={disabled} onClick={onClick}
      style={{ border: `1.5px solid ${border}`, background: bg, color: text }}>
      {children}
    </button>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', marginTop: '2.5rem', flexWrap: 'wrap' }}>
      {btn(current === 1, () => onChange(current - 1), <ChevronLeft size={14} />)}
      {visible[0] > 1 && (
        <>
          <button className="mg-pgbtn" onClick={() => onChange(1)} style={{ border: `1.5px solid ${border}`, background: bg, color: text }}>1</button>
          {visible[0] > 2 && <span style={{ color: text, fontSize: '0.8rem', opacity: 0.5 }}>…</span>}
        </>
      )}
      {visible.map(p => (
        <button key={p} className="mg-pgbtn" onClick={() => onChange(p)}
          style={{ border: `1.5px solid ${p === current ? accent : border}`, background: p === current ? accent : bg, color: p === current ? '#fff' : text }}>
          {p}
        </button>
      ))}
      {visible[visible.length - 1] < total && (
        <>
          {visible[visible.length - 1] < total - 1 && <span style={{ color: text, fontSize: '0.8rem', opacity: 0.5 }}>…</span>}
          <button className="mg-pgbtn" onClick={() => onChange(total)} style={{ border: `1.5px solid ${border}`, background: bg, color: text }}>{total}</button>
        </>
      )}
      {btn(current === total, () => onChange(current + 1), <ChevronRight size={14} />)}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   MAIN EXPORT
   Props:
     isDark         — boolean, light/dark theme
     fallbackItems  — optional array shown on fetch error
═══════════════════════════════════════════════════════════ */
export default function MbuziPlusGallery({ isDark = false, fallbackItems = [] }) {
  const [allItems,  setAllItems]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [activeTag, setActiveTag] = useState('All');
  const [page,      setPage]      = useState(1);
  const [cols,      setCols]      = useState(3);
  const [lightbox,  setLightbox]  = useState(null);
  const containerRef = useRef(null);

  useEffect(() => { injectCSS(isDark); }, [isDark]);

  /* ── Fetch from Firestore ── */
  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, 'mbuziplusprogram', 'gallery'));
      if (!snap.exists()) { setAllItems([]); return; }
      const data = snap.data();

      let flat = [];
      if (data.items && Array.isArray(data.items)) {
        flat = data.items.filter(it => it.src);
      } else if (data.albums && Array.isArray(data.albums)) {
        flat = data.albums
          .filter(a => a.visible !== false)
          .flatMap(a =>
            (a.items || []).filter(it => it.src).map(it => ({
              ...it,
              tag: it.tag || a.tag || '',
              caption: it.caption || '',
            }))
          );
      }

      setAllItems(assignAspects(flat));
    } catch (e) {
      console.error('Gallery fetch error:', e);
      setError('Could not load gallery. Please check your connection.');
      setAllItems(assignAspects(fallbackItems));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  /* ── Responsive columns ── */
  useEffect(() => {
    const measure = () => {
      const w = containerRef.current?.offsetWidth || window.innerWidth;
      if      (w < 480)  setCols(1);
      else if (w < 700)  setCols(2);
      else if (w < 1050) setCols(3);
      else               setCols(4);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => { setPage(1); }, [activeTag]);

  const scrollToTop = useCallback(() =>
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), []);

  /* ── Dimension-resolved callback ──
     When any card discovers its image's natural size, update
     allItems so the card re-renders with the correct aspect ratio.
  ── */
  const handleDimensionResolved = useCallback((src, detectedAspect) => {
    setAllItems(prev =>
      prev.map(item =>
        item.src === src && item._aspect !== detectedAspect
          ? { ...item, _aspect: detectedAspect, _dimensionsResolved: true }
          : item
      )
    );
  }, []);

  const handleLightbox = useCallback((item) => setLightbox(item), []);
  const handleLightboxClose = useCallback(() => setLightbox(null), []);

  /* ── Derived values ── */
  const tags = useMemo(() =>
    ['All', ...Array.from(new Set(allItems.map(m => m.tag).filter(Boolean)))],
    [allItems]);

  const filtered = useMemo(() =>
    activeTag === 'All' ? allItems : allItems.filter(m => m.tag === activeTag),
    [allItems, activeTag]);

  const imageOnlyFiltered = useMemo(() =>
    filtered.filter(item => item.type !== 'video'),
    [filtered]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Lightbox navigation within currently visible image-only items
  const handleLightboxNav = useCallback((newIndex) => {
    const target = imageOnlyFiltered[newIndex];
    if (target) setLightbox(target);
  }, [imageOnlyFiltered]);

  /* ── Theme tokens ── */
  const bg        = isDark ? '#0d1117' : '#f8fafc';
  const textMain  = isDark ? '#f1f5f9' : '#0f172a';
  const textSub   = isDark ? '#64748b' : '#64748b';
  const accent    = '#0f766e';
  const accentMid = isDark ? '#134e4a' : '#ccfbf1';
  const border    = isDark ? '#1e293b' : '#e2e8f0';
  const filterBg  = isDark ? '#1e293b' : '#fff';
  const filterBdr = isDark ? '#2d3f52' : '#e2e8f0';

  const headerLineStyle = {
    display: 'inline-block',
    width: 36, height: 3,
    background: accent,
    borderRadius: 2,
    verticalAlign: 'middle',
    marginRight: 10,
  };

  return (
    <section
      className="mg-root"
      ref={containerRef}
      style={{ background: bg, padding: 'clamp(3.5rem,7vw,5.5rem) 1.5rem', position: 'relative', overflow: 'hidden' }}
    >
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: isDark
          ? 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(15,118,110,0.12), transparent)'
          : 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(15,118,110,0.07), transparent)',
      }} />

      <div style={{ maxWidth: '1260px', margin: '0 auto', position: 'relative', zIndex: 1 }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: '2.8rem', maxWidth: 580 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', background: accentMid, color: accent, fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '0.68rem', letterSpacing: '0.1em', padding: '0.3rem 0.8rem', borderRadius: '4px', textTransform: 'uppercase', marginBottom: '1rem', border: `1px solid ${isDark ? 'rgba(15,118,110,0.25)' : 'rgba(15,118,110,0.2)'}` }}>
            <Images size={11} /> Gallery
          </div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 'clamp(2.1rem,5.5vw,3.4rem)', color: textMain, margin: '0 0 0.8rem', lineHeight: 1.08, letterSpacing: '-0.025em' }}>
            <span style={headerLineStyle} />
            MbuziPlus in Action
          </h2>
          <p style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.9rem', color: textSub, lineHeight: 1.8, margin: 0 }}>
            Photos and videos from training sessions, farm visits, and farmer activations across Kenya.
          </p>
        </div>

        {loading && <SkeletonGrid cols={cols} isDark={isDark} />}

        {!loading && error && (
          <div style={{ padding: '3rem 1rem', borderRadius: 12, border: `1px solid ${isDark ? '#2d1a1a' : '#fee2e2'}`, background: isDark ? '#1a0e0e' : '#fff5f5', textAlign: 'center', marginBottom: '1rem' }}>
            <p style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.85rem', color: isDark ? '#f87171' : '#dc2626', marginBottom: '1rem' }}>{error}</p>
            <button onClick={fetchItems} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', padding: '0.6rem 1.3rem', borderRadius: 8, background: accent, color: '#fff', fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: '0.8rem', border: 'none', cursor: 'pointer' }}>
              <RefreshCw size={13} /> Retry
            </button>
          </div>
        )}

        {!loading && !error && allItems.length === 0 && (
          <div style={{ textAlign: 'center', padding: '5rem 1rem', color: textSub }}>
            <Images size={40} style={{ opacity: 0.2, marginBottom: '1rem', display: 'block', margin: '0 auto 1rem' }} />
            <p style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.88rem' }}>No gallery items yet — check back soon.</p>
          </div>
        )}

        {!loading && !error && allItems.length > 0 && (
          <>
            {tags.length > 1 && (
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: `1px solid ${border}` }}>
                {tags.map(tag => {
                  const isActive = activeTag === tag;
                  const tc = tag === 'All' ? accent : getTagColor(tag);
                  const count = tag === 'All' ? allItems.length : allItems.filter(m => m.tag === tag).length;
                  return (
                    <button key={tag} className="mg-filter"
                      onClick={() => setActiveTag(tag)}
                      style={{
                        background: isActive ? tc : filterBg,
                        border: `1.5px solid ${isActive ? tc : filterBdr}`,
                        color: isActive ? '#fff' : textSub,
                        boxShadow: isActive ? `0 4px 16px ${tc}35` : 'none',
                      }}>
                      {tag}
                      <span className="mg-count-badge"
                        style={{ background: isActive ? 'rgba(255,255,255,0.2)' : (isDark ? '#0f766e22' : '#F1F5EE'), color: isActive ? '#fff' : accent }}>
                        {count}
                      </span>
                    </button>
                  );
                })}
                <span style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.68rem', color: textSub, opacity: 0.6, marginLeft: 'auto', letterSpacing: '0.04em' }}>
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} / {filtered.length}
                </span>
              </div>
            )}

            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: '4rem 1rem', color: textSub }}>
                <Images size={36} style={{ opacity: 0.2, marginBottom: '0.75rem' }} />
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.88rem' }}>No media for this category yet.</p>
              </div>
            )}

            {filtered.length > 0 && (
              <div className="mg-masonry" style={{ columnCount: cols }}>
                {pageItems.map((item, i) =>
                  item.type === 'video'
                    ? <VideoCard key={item.id || `v-${i}`} item={item} index={i} isDark={isDark} cols={cols} />
                    : <ImageCard
                        key={item.id || `img-${i}`}
                        item={item}
                        index={i}
                        isDark={isDark}
                        cols={cols}
                        onLightbox={handleLightbox}
                        onDimensionResolved={handleDimensionResolved}
                      />
                )}
              </div>
            )}

            <Pagination current={page} total={totalPages} isDark={isDark} onChange={p => { setPage(p); scrollToTop(); }} />
          </>
        )}
      </div>

      {lightbox && (
        <Lightbox
          item={lightbox}
          items={imageOnlyFiltered}
          onClose={handleLightboxClose}
          onNav={handleLightboxNav}
        />
      )}
    </section>
  );
}